import React, { useCallback, useEffect, useState } from "react";
import { stageAssignmentsAPI } from "../../api/client";

const initials = (person) =>
  `${person?.firstName?.[0] || ""}${person?.lastName?.[0] || ""}`.toUpperCase() ||
  "?";

const fullName = (person) =>
  `${person?.firstName || ""} ${person?.lastName || ""}`.trim() || "Someone";

const formatNoteDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

/**
 * Append-only note thread for a stage. Notes are keyed to the project + stage,
 * not the assignment, so every visit's notes stay visible after a return or
 * rework. Only the current assignee can add to the thread, and only while the
 * assignment is still open.
 */
export default function StageNotesSection({
  projectId,
  stageId,
  assignmentId,
  canAdd,
}) {
  const [notes, setNotes] = useState([]);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const loadNotes = useCallback(async () => {
    if (!projectId || !stageId) return;
    try {
      const resp = await stageAssignmentsAPI.getStageNotes(projectId, stageId);
      setNotes(resp.data || []);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load stage notes");
    }
  }, [projectId, stageId]);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  const addNote = async () => {
    const body = draft.trim();
    if (!body) return;
    setSaving(true);
    setError("");
    try {
      const resp = await stageAssignmentsAPI.addStageNote(
        projectId,
        assignmentId,
        body,
      );
      setNotes((prev) => [...prev, resp.data]);
      setDraft("");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to add note");
    } finally {
      setSaving(false);
    }
  };

  // Only worth labelling visits when the thread actually spans more than one.
  const spansVisits =
    new Set(notes.map((note) => note.visitNumber || 1)).size > 1;

  return (
    <div className="panel stage-notes">
      <div className="stage-notes-header">
        <h3 className="stage-notes-title">Notes</h3>
        {notes.length > 0 && (
          <span className="stage-notes-count">{notes.length}</span>
        )}
      </div>
      <p className="stage-notes-intro">
        Kept for the whole stage and never cleared — they stay visible if this
        stage comes back for rework, and appear in the project report.
      </p>

      {notes.length === 0 ? (
        <div className="stage-notes-empty">No notes yet for this stage.</div>
      ) : (
        <div className="stage-notes-list">
          {notes.map((note, index) => {
            const visit = note.visitNumber || 1;
            const previousVisit =
              index > 0 ? notes[index - 1].visitNumber || 1 : null;
            // The visit is a property of a run of notes, not of each one, so it
            // is printed once as a divider where it changes. On a stage that
            // never came back for rework it never appears at all.
            const showVisitDivider =
              spansVisits && (index === 0 || visit !== previousVisit);
            const isInstruction = note.direction === "TO_ASSIGNEE";

            return (
              <React.Fragment key={note.id}>
                {showVisitDivider && (
                  <div className="stage-note-visit-divider">
                    <span>Visit {visit}</span>
                  </div>
                )}
                <div
                  className={`stage-note-item ${
                    isInstruction ? "stage-note-item--instruction" : ""
                  }`}
                >
                  <div className="stage-note-head">
                    <span className="stage-note-avatar">
                      {initials(note.author)}
                    </span>
                    <span className="stage-note-author">
                      {fullName(note.author)}
                    </span>
                    {isInstruction && (
                      <span className="stage-note-tag">Instruction</span>
                    )}
                  </div>
                  <div className="stage-note-body">{note.body}</div>
                  <div className="stage-note-meta">
                    {formatNoteDate(note.createdAt)}
                  </div>
                </div>
              </React.Fragment>
            );
          })}
        </div>
      )}

      {canAdd && (
        <div className="stage-note-composer">
          <textarea
            rows={3}
            maxLength={5000}
            value={draft}
            placeholder="Add a note for this stage..."
            onChange={(e) => setDraft(e.target.value)}
          />
          <div className="stage-note-composer-actions">
            <button
              className="btn-primary btn-sm"
              type="button"
              disabled={saving || !draft.trim()}
              onClick={addNote}
            >
              {saving ? "Adding..." : "Add Note"}
            </button>
          </div>
        </div>
      )}

      {error && <div className="stage-notes-error">{error}</div>}
    </div>
  );
}
